
/**
 * Generates a full URL for a given path
 * @param path - The path to append to the base URL
 * @returns The complete URL
 */
export const getPageUrl = (path: string): string => {
  const baseUrl = import.meta.env.VITE_BASE_URL || 'https://app.recut.art';
  return `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
};

/**
 * Generates a full image URL for a given path
 * @param imagePath - The image path
 * @returns The complete image URL
 */
export const getImageUrl = (imagePath: string): string => {
  // If it's already an absolute URL (including our lovable uploads), return as is
  if (imagePath.startsWith('http') || imagePath.startsWith('/lovable-uploads/')) {
    return imagePath;
  }
  
  const baseUrl = import.meta.env.VITE_BASE_URL || 'https://app.recut.art';
  return `${baseUrl}${imagePath.startsWith('/') ? imagePath : `/${imagePath}`}`;
};

/**
 * Creates a slug from a string
 * @param text - The text to convert to a slug
 * @returns Slugified string suitable for URLs
 */
export const createSlug = (text: string): string => {
  return text
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/[^\w-]+/g, '') // Remove all non-word chars
    .replace(/--+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, ''); // Trim - from end of text
};

/**
 * Generates schema.org JSON-LD structured data for the current page
 * @returns JSON-LD formatted string
 */
export const generateLocalBusiness = (
  name: string,
  description: string,
  logo: string,
  url: string,
  telephone?: string,
  address?: {
    streetAddress: string;
    addressLocality: string;
    addressRegion: string;
    postalCode: string;
    addressCountry: string;
  }
) => {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": name,
    "description": description,
    "image": logo,
    "url": url,
    ...(telephone && { "telephone": telephone }),
    ...(address && { "address": {
      "@type": "PostalAddress",
      ...address
    }})
  };
};
